import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserMinus, Check, Package, FileText, Calendar as CalIcon, MessageSquare, Users, ClipboardList } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  asset_return: 'Asset Return',
  document_clearance: 'Document Clearance',
  leave_settlement: 'Leave & Settlement',
  exit_interview: 'Exit Interview',
  knowledge_transfer: 'Knowledge Transfer',
  custom: 'Custom',
};

const CATEGORY_ICONS = {
  asset_return: Package,
  document_clearance: FileText,
  leave_settlement: CalIcon,
  exit_interview: MessageSquare,
  knowledge_transfer: Users,
  custom: ClipboardList,
};

const CATEGORY_COLORS = {
  asset_return: 'from-blue-50 to-blue-100',
  document_clearance: 'from-amber-50 to-amber-100',
  leave_settlement: 'from-emerald-50 to-emerald-100',
  exit_interview: 'from-purple-50 to-purple-100',
  knowledge_transfer: 'from-pink-50 to-pink-100',
  custom: 'from-gray-50 to-gray-100',
};

const PortalOffboarding = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/employee-portal/my-offboarding`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data.offboarding);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('portal_token');
          navigate('/employee-portal/login');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-lg mx-auto text-center py-16" data-testid="no-offboarding">
        <UserMinus className="h-14 w-14 mx-auto mb-4 text-gray-200" />
        <p className="text-sm text-gray-400">No off-boarding process in progress</p>
      </div>
    );
  }

  const checklist = data.checklist || [];
  const byCategory = {};
  checklist.forEach(c => {
    if (!byCategory[c.category]) byCategory[c.category] = [];
    byCategory[c.category].push(c);
  });
  const totalItems = checklist.length;
  const completedItems = checklist.filter(c => c.completed).length;
  const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-offboarding">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
          <UserMinus className="h-5 w-5 text-red-500" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Off-boarding</h2>
          <p className="text-xs text-gray-400">
            {data.exit_reason} &middot; Status: <span className="capitalize font-medium">{data.status?.replace('_', ' ')}</span>
          </p>
        </div>
      </div>

      {/* Dates */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-rose-50">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-[10px] text-gray-400 uppercase">Resignation Date</p>
              <p className="text-sm font-semibold text-gray-800">{data.resignation_date || 'TBD'}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase">Last Working Day</p>
              <p className="text-sm font-semibold text-gray-800">{data.last_working_day || 'TBD'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-600">Progress</p>
            <p className="text-xs text-gray-400">{completedItems}/{totalItems} ({progress}%)</p>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2.5">
            <div className="bg-emerald-500 rounded-full h-2.5 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </CardContent>
      </Card>

      {/* Checklist by category */}
      {Object.entries(byCategory).map(([cat, items]) => {
        const Icon = CATEGORY_ICONS[cat] || ClipboardList;
        const catDone = items.filter(i => i.completed).length;
        const gradient = CATEGORY_COLORS[cat] || CATEGORY_COLORS.custom;
        return (
          <Card key={cat} className="border-0 shadow-sm overflow-hidden">
            <CardContent className="p-0">
              <div className={`bg-gradient-to-br ${gradient} p-3`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-gray-600" />
                    <p className="text-xs font-bold text-gray-700">{CATEGORY_LABELS[cat] || cat}</p>
                  </div>
                  <Badge variant="outline" className="text-[9px] bg-white/60">{catDone}/{items.length}</Badge>
                </div>
              </div>
              <div className="p-3 space-y-2">
                {items.map(item => (
                  <div key={item.id} className="flex items-center gap-2.5">
                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${item.completed ? 'bg-emerald-500 border-emerald-500' : 'border-gray-200'}`}>
                      {item.completed && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span className={`text-sm ${item.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>{item.item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default PortalOffboarding;
